import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SmartPaymentLinkService {
  private url = environment.api;

  constructor(private http: HttpClient) {}

  /**
   * Calls GET /api/SmartPaymentLink/by-short-link/{ShortLink}
   * Returns the SmartPaymentLink payload for the provided short link identifier.
   */
  public getByShortLink(shortLink: string): Observable<any> {
    if (!shortLink) {
      throw new Error('shortLink is required');
    }
    // Do NOT encode; backend expects raw shortLink segment
    const endpoint = `${this.url}/api/SmartPaymentLink/by-short-link/${shortLink}`;
    console.log('[SmartPaymentLinkService] fetching by short link:', endpoint);
    // Request the response as text to avoid parsing errors when backend
    // returns non-JSON (binary/base64/encrypted) payloads. Attempt to
    // JSON.parse the text and fall back to returning raw text.
    return this.http.get(endpoint, { responseType: 'text' }).pipe(
      map((text: string) => {
        if (!text) return null;
        try {
          return JSON.parse(text);
        } catch (e) {
          // Not JSON — return raw text so caller can handle binary/base64 payloads
          return text;
        }
      })
    );
  }

  /**
   * Convenience helper when the app route is baseUrl/<shortLink>.
   * Extracts the last non-empty path segment from window.location.pathname
   * and fetches the SmartPaymentLink. An optional override path can be passed
   * (useful for testing or server-side contexts).
   */
  public getByCurrentPath(pathOverride?: string): Observable<any> {
    const path =
      pathOverride ||
      (typeof window !== 'undefined' ? window.location.pathname : '');
    const parts = path.split('/').filter(Boolean);
    const shortLink = parts.length ? parts[parts.length - 1] : '';
    if (!shortLink) {
      throw new Error('No shortLink segment found in current path');
    }
    return this.getByShortLink(shortLink);
  }

  /**
   * Decrypt a base64-encoded ciphertext (AES-CBC) using a base64 key and an IV string.
   * Returns a Promise resolving to the decrypted UTF-8 string.
   * - ciphertextBase64: base64-encoded ciphertext
   * - keyBase64: base64-encoded AES key (16/24/32 bytes)
   * - ivString: either a 16-byte raw string or a base64-encoded 16-byte value
   */
  public async decryptAesBase64Text(
    ciphertextBase64: string,
    keyBase64: string,
    ivString: string
  ): Promise<string> {
    if (!ciphertextBase64) return '';
    const subtle = (globalThis.crypto &&
      (globalThis.crypto as any).subtle) as SubtleCrypto;
    if (!subtle) throw new Error('Web Crypto API is not available');

    // Helper: decode base64 to Uint8Array
    const base64ToBytes = (b64: string) => {
      const bin = atob(b64);
      const len = bin.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
      return bytes;
    };

    // Decode key (base64)
    const keyBytes = base64ToBytes(keyBase64);

    // Decode ciphertext (base64)
    const cipherBytes = base64ToBytes(ciphertextBase64);

    // Prepare IV: try to treat ivString as base64 first; if that produces 16 bytes use it, otherwise treat as raw UTF-8
    let ivBytes: Uint8Array;
    try {
      const candidate = base64ToBytes(ivString);
      if (candidate && candidate.length === 16) {
        ivBytes = candidate;
      } else {
        ivBytes = new TextEncoder().encode(ivString);
      }
    } catch (e) {
      ivBytes = new TextEncoder().encode(ivString);
    }

    // Import key
    // Ensure we pass plain ArrayBuffers (slice copies the underlying buffer)
    const keyBuffer = keyBytes.buffer.slice(
      keyBytes.byteOffset,
      keyBytes.byteOffset + keyBytes.byteLength
    );
    const subtleAny = subtle as any;
    const cryptoKey = await subtleAny.importKey(
      'raw',
      keyBuffer as any,
      { name: 'AES-CBC' },
      false,
      ['decrypt']
    );

    // Decrypt (copy ciphertext buffer to a clean ArrayBuffer)
    const cipherSlice = cipherBytes.buffer.slice(
      cipherBytes.byteOffset,
      cipherBytes.byteOffset + cipherBytes.byteLength
    );
    // Make a fresh ArrayBuffer copy to ensure it's a proper ArrayBuffer type
    const cipherCopy = new Uint8Array(cipherSlice.byteLength);
    cipherCopy.set(new Uint8Array(cipherSlice));
    const cipherArray = cipherCopy.buffer;
    const plainBuffer = await subtleAny.decrypt(
      { name: 'AES-CBC', iv: ivBytes },
      cryptoKey,
      cipherArray as any
    );
    const dec = new TextDecoder();
    return dec.decode(plainBuffer);
  }
}
